import { Loader2 } from 'lucide-react'
import { type ReactNode, type ButtonHTMLAttributes } from 'react'

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  children: ReactNode
}

/**
 * Button component with FluidifyAI Regen design system styles
 * Supports multiple variants, sizes, and loading state
 */
export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled,
  children,
  className = '',
  type = 'button',
  ...props
}: ButtonProps) {
  const baseStyles =
    'touch-target inline-flex shrink-0 items-center justify-center rounded font-medium transition-[background-color,border-color,color,box-shadow,transform] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-2 motion-reduce:transition-none active:translate-y-px disabled:cursor-not-allowed disabled:opacity-50 disabled:active:translate-y-0'

  const variantStyles = {
    primary:
      'bg-brand-primary text-white shadow-sm hover:bg-brand-primary-hover active:bg-brand-primary-hover',
    secondary:
      'border border-border bg-surface-primary text-text-primary shadow-sm hover:bg-surface-secondary hover:border-border-strong',
    ghost: 'bg-transparent text-text-secondary hover:bg-surface-tertiary hover:text-text-primary',
    danger: 'bg-red-600 text-white shadow-sm hover:bg-red-700 active:bg-red-800',
  }

  const sizeStyles = {
    sm: 'min-h-8 px-3 text-sm',
    md: 'min-h-9 px-3.5 text-base',
    lg: 'min-h-11 px-5 text-lg',
  }

  const gapStyles = {
    sm: 'gap-1.5',
    md: 'gap-2',
    lg: 'gap-2.5',
  }

  return (
    <button
      className={`${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${className}`}
      disabled={disabled || loading}
      type={type}
      {...props}
    >
      <span className={`inline-flex items-center ${gapStyles[size]}`}>
        {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
        {children}
      </span>
    </button>
  )
}
